import { useNavigate } from 'react-router-dom'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import LandingNav from './LandingNav'
import BlueprintGrid from './blueprint/BlueprintGrid'
import HeroWorkspace from './blueprint/HeroWorkspace'
import SpecStrip from './blueprint/SpecStrip'
import HowItWorks from './blueprint/HowItWorks'
import FeatureBento from './blueprint/FeatureBento'
import Comparison from './blueprint/Comparison'
import CadableSection from './blueprint/CadableSection'
import ClosingCta from './blueprint/ClosingCta'
import SiteFooter from './blueprint/SiteFooter'
import { Rails, Reveal } from './blueprint/Section'
import { INVITE_HREF, primaryButton, secondaryButton } from './blueprint/ui'

const PRODUCTS = [
  { n: '01', name: 'GCodr', title: 'Drawings to G-code', body: 'DXF, DWG, STEP or PDF in. G-code out.', href: '#how' },
  { n: '02', name: 'CADable', title: 'Photos to parts', body: 'Measure, model, then print or machine.', href: '#cadable' },
]

export default function LandingPage() {
  const navigate = useNavigate()
  return (
    <div className="relative flex min-h-screen flex-col overflow-x-clip bg-[#030712] text-slate-300">
      <LandingNav />
      <div className="flex-1 pt-14">
        <Rails>
          <main>
            <section aria-labelledby="hero-title" className="relative px-5 pb-16 pt-14 md:px-12 md:pb-24 md:pt-24">
              <BlueprintGrid fade />
              <div className="relative grid gap-12 lg:grid-cols-[1fr_260px] lg:items-end">
                <Reveal>
                  <a href={INVITE_HREF} className="bp-focus inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/[0.06] py-1 pl-2.5 pr-3 font-mono text-[11px] text-cyan-300 transition-colors duration-150 hover:border-cyan-500/50">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                    Invite-only preview<span className="hidden sm:inline"> · Now onboarding CNC shops</span>
                    <ArrowRight className="h-3 w-3" />
                  </a>
                  <h1 id="hero-title" className="mt-8 font-display text-[64px] font-semibold leading-[0.92] tracking-[-0.04em] text-slate-50 sm:text-[88px] md:text-[112px]">
                    Make{' '}
                    <span style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', filter: 'drop-shadow(0 0 24px rgba(6,182,212,0.35))' }}>
                      the part.
                    </span>
                  </h1>
                  <p className="mt-7 max-w-2xl font-display text-xl leading-snug text-slate-500 md:text-[22px]">
                    <span className="text-slate-100">From drawing to G-code, without a CAM seat.</span>{' '}
                    GCodr turns engineering drawings into verified CNC programs. CADable turns photos into parts you can print or machine.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-3">
                    <button type="button" onClick={() => navigate('/login')} className={primaryButton}>Open GCodr <ArrowRight className="h-4 w-4" /></button>
                    <a href={INVITE_HREF} className={secondaryButton}>Request an invite</a>
                  </div>
                </Reveal>
                <Reveal delay={0.1} className="hidden lg:block">
                  <ol className="space-y-6 border-l border-white/[0.08] pl-6">
                    {PRODUCTS.map(p => (
                      <li key={p.n}>
                        <a href={p.href} className="bp-focus group block">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{p.n} · {p.name}</span>
                          <span className="mt-1 flex items-center gap-1.5 font-display text-lg font-semibold text-slate-100 transition-colors duration-150 group-hover:text-cyan-300">
                            {p.title}<ArrowUpRight className="h-4 w-4 text-slate-600 transition-colors duration-150 group-hover:text-cyan-400" />
                          </span>
                          <span className="mt-1 block font-display text-sm leading-relaxed text-slate-500">{p.body}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </Reveal>
              </div>
              <Reveal delay={0.15} className="relative mt-16 md:mt-20">
                <HeroWorkspace />
              </Reveal>
            </section>
            <SpecStrip />
            <HowItWorks />
            <FeatureBento />
            <Comparison />
            <CadableSection />
            <ClosingCta />
          </main>
          <SiteFooter />
        </Rails>
      </div>
    </div>
  )
}
